import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useAppSelector } from "../globalState/hooks";
import { CartLine, CartProduct, CartState } from "../types/cart";
import {
  calculateCartTotals,
  cartStorageKey,
  effectiveCap,
  emptyCart,
  loadCartFromStorage,
  mergeGuestCartIfNeeded,
  saveCartToStorage,
} from "../utils/cartPersistence";
import { deleteHeldOrderOnce } from "../utils/checkoutOrder";

type CartAction =
  | { type: "HYDRATE"; payload: CartState }
  | { type: "SET_ITEMS"; payload: CartLine[] }
  | {
      type: "ADD_OR_UPDATE";
      payload: { product: CartProduct; quantity: number };
    }
  | { type: "REMOVE"; payload: string }
  | { type: "CLEAR" };

interface CartContextValue {
  cart: CartState;
  hydrated: boolean;
  addOrUpdateItem: (product: CartProduct, quantity: number) => void;
  removeItem: (productId: string) => void;
  replaceItems: (items: CartLine[]) => void;
  clearCart: () => void;
  getItemQuantity: (productId: string) => number;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "HYDRATE":
    case "SET_ITEMS":
      return action.type === "HYDRATE"
        ? action.payload
        : calculateCartTotals(action.payload);
    case "ADD_OR_UPDATE": {
      const { product, quantity } = action.payload;
      const price = Number(product.price);
      if (!Number.isFinite(price) || price <= 0) return state;

      const cap = effectiveCap(product);
      if (quantity > 0 && cap < 1 && !state.items.some((l) => l.product.id === product.id)) {
        return state;
      }

      let qty = Math.floor(quantity);
      if (!Number.isFinite(qty)) return state;
      if (qty > 0) qty = Math.min(qty, Math.max(cap, 1));

      const next = state.items.filter((line) => line.product.id !== product.id);
      if (qty > 0) {
        next.push({ product: { ...product, price }, quantity: qty });
      }
      return calculateCartTotals(next);
    }
    case "REMOVE":
      return calculateCartTotals(
        state.items.filter((line) => line.product.id !== action.payload)
      );
    case "CLEAR":
      return emptyCart();
    default:
      return state;
  }
};

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const login = useAppSelector((s) => s.authReducer.loginDetials);
  const userKey = useMemo(
    () => cartStorageKey(login?.id, login?.email ?? login?.userName),
    [login?.id, login?.email, login?.userName]
  );
  const [cart, dispatch] = useReducer(cartReducer, emptyCart());
  const [hydrated, setHydrated] = React.useState(false);
  const prevKeyRef = useRef<string | null>(null);
  const sawItemsRef = useRef(false);

  useEffect(() => {
    if (login?.id) {
      mergeGuestCartIfNeeded(userKey);
    }
    const loaded = loadCartFromStorage(userKey);
    dispatch({ type: "HYDRATE", payload: loaded });
    prevKeyRef.current = userKey;
    setHydrated(true);
  }, [userKey, login?.id]);

  useEffect(() => {
    if (!hydrated) return;
    if (prevKeyRef.current !== userKey) return;
    saveCartToStorage(userKey, cart);
  }, [cart, hydrated, userKey]);

  useEffect(() => {
    if (!hydrated) return;
    if (cart.items.length > 0) {
      sawItemsRef.current = true;
      return;
    }
    if (!sawItemsRef.current) return;
    sawItemsRef.current = false;
    void deleteHeldOrderOnce();
  }, [hydrated, cart.items.length]);

  const addOrUpdateItem = useCallback((product: CartProduct, quantity: number) => {
    dispatch({ type: "ADD_OR_UPDATE", payload: { product, quantity } });
  }, []);

  const removeItem = useCallback((productId: string) => {
    dispatch({ type: "REMOVE", payload: productId });
  }, []);

  const replaceItems = useCallback((items: CartLine[]) => {
    dispatch({ type: "SET_ITEMS", payload: items });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR" });
  }, []);

  const getItemQuantity = useCallback(
    (productId: string) =>
      cart.items.find((line) => line.product.id === productId)?.quantity ?? 0,
    [cart.items]
  );

  const value = useMemo(
    () => ({
      cart,
      hydrated,
      addOrUpdateItem,
      removeItem,
      replaceItems,
      clearCart,
      getItemQuantity,
    }),
    [cart, hydrated, addOrUpdateItem, removeItem, replaceItems, clearCart, getItemQuantity]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
